/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useCallback, useContext, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { object, string } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Button, { Variant } from '../button'
import FormStep1 from './form-step-1'
import FormStep2 from './form-step-2'
import FormStep3 from './form-step-3'
import PageContainer from 'components/page-container'
import { FormActionsContainer, StyledForm, StyledFormContainer, StyledHeading, StyledParagraph } from './style'
import axios from 'axios'
import FormStep5 from './form-step-5'
import { IProduct, ProductContext } from 'context/productsContext'
import Check from '../../assets/icons/check.svg'
import styled from 'styled-components'
import FormStep4 from './form-step-4'
import { StyledError } from 'components/form-fields/shared-style'
import { ParagraphTypes } from 'theme/basic/typography'
import Loader from 'components/loader'
import { translate } from 'locales/i18n'

export enum formFieldNames {
  CURRENT_STEP = 'currentStep',
  APPLICATION_TYPE = 'applicationType',

  FEATURES = 'features',
  BACKEND_FEATURES = 'backendFeatures',
  BUDGET = 'budget',
  DESCRIPTION = 'description',

  // Organization
  ORGANIZATION_NAME = 'organizationName',

  // Person
  PERSON_NAME = 'personName',
  PERSON_EMAIL = 'personEmail',
  PERSON_PHONE = 'personPhone',
}

const API_TOKEN = process.env.REACT_APP_API_TOKEN ?? ''
const FORM_STEP_COUNT = 5
const BASE_URL = 'https://api.pipedrive.com/v1'

const formPartComponentLookupTable: {
  [key: number]: typeof FormStep1 | typeof FormStep2 | typeof FormStep3 | typeof FormStep4 | typeof FormStep5
} = {
  1: FormStep1,
  2: FormStep2,
  3: FormStep3,
  4: FormStep4,
  5: FormStep5
}

const CostCalculatorForm: React.FC = () => {
  const {
    allProducts,
    mobileBaseProduct,
    webBaseProduct,
    mobileAndWebBaseProduct,
    pagePriceProduct
  } = useContext(ProductContext)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [stepError, setStepError] = useState<string | null>(null)

  const schema = object({
    [formFieldNames.APPLICATION_TYPE]: string().required(
      'Application type is required'
    ),
    [formFieldNames.PERSON_NAME]: string().required(translate('forms.error')),
    [formFieldNames.ORGANIZATION_NAME]: string().required(translate('forms.error')),
    [formFieldNames.PERSON_EMAIL]: string().email(translate('forms.emailNotValid')).required(translate('forms.error')),
    [formFieldNames.BUDGET]: string().required(translate('forms.error')),
    [formFieldNames.DESCRIPTION]: string(),
    [formFieldNames.FEATURES]: object().required(translate('forms.error'))
  }).required()

  const methods = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      [formFieldNames.APPLICATION_TYPE]: translate('forms.step1.offerType.option1'),
      [formFieldNames.BUDGET]: '',
      [formFieldNames.FEATURES]: {},
      [formFieldNames.BACKEND_FEATURES]: {},
      [formFieldNames.ORGANIZATION_NAME]: '',
      [formFieldNames.DESCRIPTION]: '',
      [formFieldNames.PERSON_NAME]: '',
      [formFieldNames.PERSON_EMAIL]: '',
      [formFieldNames.PERSON_PHONE]: '',
      [formFieldNames.CURRENT_STEP]: 1
    }
  })

  const { handleSubmit, watch, setValue } = methods

  const watchCurrentStep = watch(formFieldNames.CURRENT_STEP)
  const watchFeatures = watch(formFieldNames.FEATURES)
  const watchBackendFeatures = watch(formFieldNames.BACKEND_FEATURES)
  const watchBudget = watch(formFieldNames.BUDGET)

  const FormPartComponent = formPartComponentLookupTable[watchCurrentStep]

  async function createOrganization (organizationName: string) {
    return await axios.post(`${BASE_URL}/organizations?api_token=${API_TOKEN}`, {
      name: organizationName,
      visible_to: '3'
    })
  }
  async function createPerson (personName: string, personEmail: string, personPhone: string, orgId: number) {
    return await axios.post(`${BASE_URL}/persons?api_token=${API_TOKEN}`, {
      name: personName,
      visible_to: '3',
      marketing_status: 'subscribed',
      org_id: orgId,
      email: [
        {
          value: personEmail,
          primary: 'true',
          label: personEmail
        }
      ],
      phone: [
        {
          value: personPhone,
          primary: 'true',
          label: personPhone
        }
      ]
    })
  }
  async function createLead (calculatedAmount: number, personId: number, orgId: number, organizationName: string, personName: string, category: string) {
    return await axios.post(`${BASE_URL}/leads?api_token=${API_TOKEN}`, {
      title: `${organizationName} - ${personName}`,
      visible_to: '3',
      was_seen: false,
      person_id: personId,
      organization_id: orgId,
      value: {
        amount: calculatedAmount,
        currency: 'EUR'
      },
      a909f000b7635f3af3993d1c45a96bf646a32e19: category
    })
  }
  async function createNote (leadId: string, personId: number, content: string) {
    return await axios.post(`${BASE_URL}/notes?api_token=${API_TOKEN}`, {
      content,
      lead_id: leadId,
      person_id: personId
    })
  }

  function getObjectKeysWithTrueValues (obj: { [key: string]: boolean }): string[] {
    return Object.keys(obj).filter(key => obj[key])
  }

  function calculateTotal (items: IProduct[]): number {
    return items.reduce((total, item) => total + item.prices[0].price, 0)
  }

  const calculateTotalAmount = useCallback((applicationType: string, basePrice: number, featureList: { [k: string]: boolean }, backendFeatureList: { [k: string]: boolean }) => {
    let total = basePrice

    const productNames = getObjectKeysWithTrueValues({ ...featureList, ...backendFeatureList })
    const finalProducts = allProducts.filter(product => productNames.includes(product.name))
    const productsTotal = calculateTotal(finalProducts)

    total += productsTotal

    return total
  },
  [allProducts, pagePriceProduct]
  )

  // !todo: here check func. 90k -> 90k - 100k
  function nearestMultiples (num: number): string {
    let lowerMultipleK: number, upperMultipleK: number

    if (num % 10000 === 0) {
      lowerMultipleK = num / 1000
      upperMultipleK = (num + 10000) / 1000
    } else {
      lowerMultipleK = Math.floor(num / 10000) * 10
      upperMultipleK = Math.ceil(num / 10000) * 10
    }

    return `${lowerMultipleK}K - ${upperMultipleK}K`
  }

  const handleFormSubmit = useCallback(
    handleSubmit(async ({
      applicationType,
      organizationName,
      personName,
      personEmail,
      personPhone,
      budget,
      features,
      backendFeatures,
      description
    }) => {
      const basePrice = (applicationType === translate('forms.step1.offerType.option1') ? mobileBaseProduct?.prices[0].price : applicationType === translate('forms.step1.offerType.option2') ? webBaseProduct?.prices[0].price : mobileAndWebBaseProduct?.prices[0].price) ?? 0
      const featureList = features
      const backendFeatureList = backendFeatures
      const totalAmount = calculateTotalAmount(applicationType, basePrice, featureList, backendFeatureList)
      const productNames = getObjectKeysWithTrueValues({ ...featureList, ...backendFeatureList })
      setIsSubmitting(true)
      try {
        const orgData = await createOrganization(organizationName)
        if (orgData.data.success) {
          const personData = await createPerson(personName, personEmail, personPhone, orgData.data.data.id)
          const category = nearestMultiples(Number(totalAmount)).replaceAll('000', '')
          if (personData.data.success) {
            const leadData = await createLead(totalAmount, personData.data.data.id, orgData.data.data.id, organizationName, personName, category)

            if (leadData.data.success) {
              const content = translate('forms.newLead.leadDataContent', {
                personName,
                organizationName,
                applicationType,
                leadIdeaDescription: description ? translate('forms.newLead.leadIdeaDescription', { description }) : '',
                budget,
                category,
                productNames: productNames.join(', ')
              })

              const noteData = await createNote(leadData.data.data.id, personData.data.data.id, content)
              if (noteData.data.success) {
                setIsSubmitting(false)
                setIsSuccess(true)
              } else {
                throw new Error('error')
              }
            } else {
              throw new Error('error')
            }
          } else {
            throw new Error('error')
          }
        } else {
          throw new Error('error')
        }
      } catch (err) {
        console.log(err)
      }
    }, (err) => {
      console.log('Error', err)
    }),
    [handleSubmit, mobileBaseProduct, webBaseProduct, mobileAndWebBaseProduct]
  )

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const handleNextClick = useCallback(
    () => {
      setStepError(null)
      if (watchCurrentStep === 1) {
        setValue(formFieldNames.CURRENT_STEP, watchCurrentStep + 1)
      } else if (watchCurrentStep === 2 && Object.values(watchFeatures).some(value => !!value)) {
        setValue(formFieldNames.CURRENT_STEP, watchCurrentStep + 1)
      } else if (watchCurrentStep === 3) {
        setValue(formFieldNames.CURRENT_STEP, watchCurrentStep + 1)
      } else if (watchCurrentStep === 4 && !!watchBudget) {
        setValue(formFieldNames.CURRENT_STEP, watchCurrentStep + 1)
      } else {
        setStepError(translate('forms.error'))
      }
      goToTop()
    },
    [setValue, watchCurrentStep, watchFeatures, watchBackendFeatures, watchBudget, setStepError]
  )

  return (
    <PageContainer>
      {isSubmitting
        ? <StyledLoaderContainer><Loader /></StyledLoaderContainer>
        : isSuccess
          ? <SuccessContainer>
            <StyledCheckImg src={Check} />
          <StyledHeading>{translate('forms.success')}</StyledHeading>
        <StyledParagraph>{translate('forms.nextStepInstruction')}</StyledParagraph>
        </SuccessContainer>
          : <StyledFormContainer>
        <FormProvider {...methods}>
          <StyledForm onSubmit={handleFormSubmit}>
            <FormPartComponent />
            {stepError && <StyledError type={ParagraphTypes.PARAGRAPH_HIGHLIGHT}>
          {stepError}
        </StyledError>}

            <FormActionsContainer>
            {watchCurrentStep !== 1
              ? <Button
                  id='previous-button'
                  type='button'
                  variant={Variant.SECONDARY}
                  label={translate('forms.previous')}
                  onClick={() => {
                    setStepError(null)
                    setValue(formFieldNames.CURRENT_STEP, watchCurrentStep - 1)
                  }}
                />
              : <div />}
              {watchCurrentStep < FORM_STEP_COUNT && (
                <Button
                  id='next-button'
                  type='button'
                  label={translate('forms.next')}
                  onClick={handleNextClick}
                />
              )}
              {watchCurrentStep === FORM_STEP_COUNT && (
                <Button
                  id='submit-button'
                  type='submit'
                  label={translate('forms.submit')}
                  isLoading={isSubmitting}
                />
              )}
            </FormActionsContainer>
          </StyledForm>
        </FormProvider>
      </StyledFormContainer>
      }
    </PageContainer>
  )
}

export default CostCalculatorForm

const StyledCheckImg = styled.img`
  width: 80px;
  aspect-ratio: 1;
  margin: 30px auto;
  display: block;
  background: #3637df14;
  padding: 20px;
  border-radius: 50%;
`

const SuccessContainer = styled.div`
h1, p {
  text-align: center;
}
`
const StyledLoaderContainer = styled.div`
height: 60vh;
display: grid;
justify-content: center;
align-items: center;
`
